import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, UserPlus, FileText, Users, Sparkles, PlayCircle,
  SlidersHorizontal, MessageSquare, BookOpen, Compass, Star, Rocket, UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.pexels.com/photos/7414216/pexels-photo-7414216.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=1000";

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, y = 26, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

// macOS-style browser frame for the product screenshots
const BrowserFrame = ({ src, alt, dark = false, url = "startupotter.com", testId }) => (
  <div
    data-testid={testId}
    className={`overflow-hidden rounded-2xl border shadow-[0_24px_60px_-15px_rgba(16,24,40,0.35)] ${
      dark ? "border-white/10 bg-[#0b1220] ring-1 ring-primary/30" : "border-border bg-card"
    }`}
  >
    <div className={`flex items-center gap-2 border-b px-4 py-3 ${dark ? "border-white/10 bg-white/[0.03]" : "border-border bg-secondary/60"}`}>
      <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
      <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
      <span className="h-3 w-3 rounded-full bg-[#28c840]" />
      <div className={`ml-3 hidden flex-1 rounded-md px-3 py-1 text-xs sm:block ${dark ? "bg-white/[0.06] text-white/50" : "bg-card text-muted-foreground"}`}>
        {url}
      </div>
    </div>
    <img src={src} alt={alt} loading="lazy" className="block w-full" />
  </div>
);

const steps = [
  { icon: UserPlus, n: "01", title: "Create an account", desc: "Join the ecosystem and set up your foundational profile in minutes." },
  { icon: FileText, n: "02", title: "Complete a profile", desc: "Showcase your venture — video, industries, insights, and what you need." },
  { icon: Users, n: "03", title: "Start connecting", desc: "Find the right people, businesses, and capital and grow together." },
];

const chatterTags = [
  { label: "Requests", cls: "bg-primary/10 text-primary ring-1 ring-primary/20" },
  { label: "Insights", cls: "bg-[hsl(210_55%_42%/0.12)] text-[hsl(210_55%_34%)] ring-1 ring-[hsl(210_55%_42%/0.2)]" },
  { label: "Offers", cls: "bg-[hsl(160_55%_38%/0.14)] text-[hsl(160_55%_28%)] ring-1 ring-[hsl(160_55%_38%/0.22)]" },
  { label: "Milestones", cls: "bg-[hsl(330_65%_55%/0.12)] text-[hsl(330_65%_45%)] ring-1 ring-[hsl(330_65%_55%/0.2)]" },
];

export default function Home() {
  return (
    <div className="overflow-x-hidden" data-testid="home-page">
      {/* ============ HERO ============ */}
      <section
        className="relative"
        style={{ background: "radial-gradient(1200px 500px at 15% -10%, hsl(182 60% 88% / 0.9), transparent 60%), linear-gradient(160deg, #E7F6F6 0%, #F5FAFA 50%, #FFFFFF 100%)" }}
      >
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pt-20 pb-3 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:pt-28 lg:pb-4 lg:px-8 min-[1900px]:pb-8">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-xs font-[560] text-primary ring-1 ring-teal-100">
                <Sparkles className="h-3.5 w-3.5" /> Welcome to Startup Otter
              </span>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="mt-5 text-4xl font-[700] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-6xl lg:leading-[1.05]">
                Helping the Startup Ecosystem Thrive
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 max-w-xl text-base leading-8 text-muted-foreground sm:text-lg">
                Startup Otter is your people-powered platform for community, connection, and collaboration.
                Find the right people, in the right space, at the right time to help your venture move to the next level.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link to="/register">
                  <Button size="lg" className="w-full rounded-full px-8 sm:w-auto" data-testid="hero-primary-cta">
                    Get started free <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/people">
                  <Button size="lg" variant="secondary" className="w-full rounded-full px-8 sm:w-auto" data-testid="hero-secondary-cta">
                    <Compass className="mr-2 h-4 w-4" /> Explore the directory
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} y={34}>
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-primary/10 blur-2xl" />
              <div className="overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-[0_30px_70px_-20px_rgba(16,24,40,0.3)]">
                <img src={HERO_IMG} alt="Founders collaborating" className="h-[300px] w-full object-cover sm:h-[440px]" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ LAUNCH BANNER ============ */}
      <section
        className="relative overflow-hidden border-y border-[#148b8f]/20 bg-[#040d12] text-white"
        data-testid="home-launch-banner"
      >
        <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[#148b8f]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-0 -left-32 h-96 w-96 rounded-full bg-[#148b8f]/10 blur-3xl" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-6 text-center sm:flex-row sm:justify-center sm:gap-4 sm:px-6 sm:py-7 sm:text-left lg:px-8">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur">
            <Rocket className="h-5 w-5 text-[#2dd4bf]" />
          </span>
          <p className="text-lg font-[600] leading-snug text-white sm:text-xl" data-testid="home-launch-banner-text">
            We&apos;re launched and live!{" "}
            <Link
              to="/register"
              className="whitespace-nowrap font-[720] text-[#2dd4bf] underline decoration-[#2dd4bf]/40 decoration-2 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
              data-testid="home-launch-banner-cta"
            >
              Create a FREE account
            </Link>{" "}
            today!
          </p>
        </div>
      </section>

      {/* ============ GETTING STARTED ============ */}
      <section className="bg-card" data-testid="home-getting-started">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-sm font-[600] uppercase tracking-[0.14em] text-primary">Get started</span>
              <h2 className="mt-3 text-3xl font-[680] tracking-[-0.01em] text-foreground sm:text-4xl">Getting started is easy.</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="group relative h-full rounded-2xl border border-border bg-background/60 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_20px_40px_-20px_rgba(16,24,40,0.25)]">
                  <span className="absolute right-6 top-6 text-4xl font-[700] text-primary/10 transition-colors group-hover:text-primary/20">{s.n}</span>
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-[640] text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURE 1 — ROBUST PROFILES (dark, high contrast) ============ */}
      <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="home-feature-profiles">
        <div className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full bg-primary/20 blur-[100px]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 text-xs font-[560] text-teal-200 ring-1 ring-white/10">
                <PlayCircle className="h-3.5 w-3.5" /> Profiles
              </span>
              <h2 className="mt-5 text-3xl font-[680] tracking-[-0.01em] sm:text-4xl lg:leading-[1.15]">
                Robust profiles that feature you with video, industries, and insights.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-8 text-white/70">
                Whether you're a startup, business, accelerator, or VC, enjoy profiles that highlight the key needs
                of your organization tailored to the startup ecosystem.
              </p>
              <Link to="/register">
                <Button size="lg" className="mt-8 rounded-full bg-white px-8 text-primary hover:bg-white/90" data-testid="feature-profiles-cta">
                  Build your profile <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12} y={34}>
            <BrowserFrame src="/home/profile.png?v=3" alt="A Startup Otter profile" dark url="startupotter.com/p/maya-chen" testId="screenshot-profile" />
          </Reveal>
        </div>
      </section>

      {/* ============ FEATURE 2 — DIRECTORY SEARCH (image left) ============ */}
      <section className="bg-background" data-testid="home-feature-directory">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <Reveal y={34} className="lg:order-1">
            <BrowserFrame src="/home/directory.png?v=3" alt="Directory search filters for businesses" url="startupotter.com/businesses" testId="screenshot-directory" />
          </Reveal>
          <Reveal delay={0.1} className="lg:order-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-xs font-[560] text-primary ring-1 ring-teal-100">
                <SlidersHorizontal className="h-3.5 w-3.5" /> Discover
              </span>
              <h2 className="mt-5 text-3xl font-[680] tracking-[-0.01em] text-foreground sm:text-4xl lg:leading-[1.15]">
                Finding the right people got a whole lot easier.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-8 text-muted-foreground">
                Research startups, businesses, and capital opportunities based on a variety of traits — from location
                to industry, to check size, to stage and more.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/businesses"><Button variant="secondary" className="rounded-full" data-testid="feature-directory-cta">Browse businesses <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
                <Link to="/people"><Button variant="ghost" className="rounded-full" data-testid="feature-directory-people-cta">Meet the people</Button></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ CHATTER (centered) ============ */}
      <section className="bg-card" data-testid="home-chatter">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal>
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-[560] text-primary">
                <MessageSquare className="h-3.5 w-3.5" /> Chatter
              </span>
              <h2 className="mt-4 text-3xl font-[680] tracking-[-0.01em] text-foreground sm:text-4xl">Conversations with purpose.</h2>
              <p className="mt-5 text-base leading-8 text-muted-foreground sm:text-lg">
                Share posts tailored to categories such as requests, insights, offers, and more, so each post brings
                purpose instead of just endless scrolling.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {chatterTags.map((t) => (
                  <span key={t.label} className={`rounded-full px-3 py-1 text-xs font-[560] ${t.cls}`}>{t.label}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} y={34}>
            <div className="mx-auto mt-12 max-w-5xl">
              <BrowserFrame src="/home/chatter.png?v=3" alt="The Startup Otter Chatter feed" url="startupotter.com/chatter" testId="screenshot-chatter" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ OTTER ASSIST (dark, flagship) ============ */}
      <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="home-otter-assist">
        <div className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-primary/25 blur-[110px]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1 text-xs font-[560] text-teal-200 ring-1 ring-white/10">
                <Sparkles className="h-3.5 w-3.5" /> Otter Assist
              </span>
              <h2 className="mt-5 text-3xl font-[680] tracking-[-0.01em] sm:text-4xl lg:leading-[1.15]">
                Somebody out there can help. Otter Assist goes and finds them.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-8 text-white/70">
                Post what you&apos;re stuck on and Otter Assist works the community for you — reading the ask,
                weighing who genuinely fits, and putting it in front of a handful of people at a time.
              </p>
              <p className="mt-4 text-sm font-[600] text-teal-300">No cold blasts. No spray and pray. Just people worth asking.</p>
              <Link to="/features/otter-assist">
                <Button size="lg" className="mt-8 rounded-full bg-white px-8 text-primary hover:bg-white/90" data-testid="home-otter-assist-cta">
                  See how it works <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12} y={34}>
            <BrowserFrame
              src="/features/otter-assist-needs.png"
              alt="Community Needs delivered by Otter Assist, showing why you matched and the I Can Help action"
              dark
              url="startupotter.com/dashboard/otter-assist"
              testId="screenshot-otter-assist"
            />
          </Reveal>
        </div>
      </section>

      {/* ============ GROUPS (image left) ============ */}
      <section className="bg-background" data-testid="home-groups">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <Reveal y={34} className="lg:order-1">
            <BrowserFrame
              src="/features/group-page.png"
              alt="A Startup Otter Group with its streams, members and events"
              url="startupotter.com/g/front-range-founders"
              testId="screenshot-groups"
            />
          </Reveal>
          <Reveal delay={0.1} className="lg:order-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1 text-xs font-[560] text-primary ring-1 ring-teal-100">
                <UsersRound className="h-3.5 w-3.5" /> Groups
              </span>
              <h2 className="mt-5 text-3xl font-[680] tracking-[-0.01em] text-foreground sm:text-4xl lg:leading-[1.15]">
                Your corner of the ecosystem, with its own front door.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-8 text-muted-foreground">
                Start a Group for your accelerator, meetup, fund portfolio or founder circle. Organize the
                conversation into streams, keep members-only events and resources in one place, and decide who gets
                in — open to all, by approval, or by invitation only.
              </p>
              <p className="mt-4 text-sm font-[600] text-primary">A real home for your community — not another chat thread.</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/features/groups">
                  <Button size="lg" className="rounded-full px-8" data-testid="home-groups-cta">
                    Explore Groups <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/groups">
                  <Button size="lg" variant="ghost" className="rounded-full" data-testid="home-groups-browse-cta">
                    Browse all groups
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ RESOURCES & EVENTS (primary band) ============ */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground" data-testid="home-resources">
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: "radial-gradient(600px 300px at 85% 20%, rgba(255,255,255,0.5), transparent 60%)" }} />
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-24">
          <Reveal>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
              <BookOpen className="h-6 w-6" />
            </div>
            <p className="mx-auto mt-6 max-w-2xl text-lg font-[560] leading-8 sm:text-xl">
              Top it all off with community-sourced and rated tools, resources, and events that are happening right in
              your backyard or all over the globe.
            </p>
            <Link to="/resources">
              <Button size="lg" className="mt-8 rounded-full bg-white px-8 text-primary hover:bg-white/90" data-testid="resources-cta">
                Check Out Resources <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <div className="mt-6 flex items-center justify-center gap-1.5 text-sm text-white/80">
              <Star className="h-4 w-4 fill-white/80 text-white/80" /> Community-rated tools, guides & events
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-background" data-testid="home-final-cta">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal y={30}>
            <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card p-10 text-center shadow-[0_24px_60px_-30px_rgba(16,24,40,0.3)] lg:p-16">
              <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
              <h2 className="relative text-3xl font-[700] tracking-[-0.01em] text-foreground sm:text-4xl">
                Build something that lasts, together.
              </h2>
              <p className="relative mx-auto mt-5 max-w-2xl text-base leading-8 text-muted-foreground sm:text-lg">
                Startup Otter helps facilitate meaningful connections and resources with the right people just for you.
                Join us in building a collaborative environment that helps everyone in the startup ecosystem thrive.
              </p>
              <div className="relative mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link to="/register">
                  <Button size="lg" className="w-full rounded-full px-10 sm:w-auto" data-testid="final-cta-button">
                    Join Startup Otter <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/people">
                  <Button size="lg" variant="ghost" className="w-full rounded-full sm:w-auto" data-testid="final-cta-secondary">
                    Take a look around
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
